import { useEffect, useMemo, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import {
  Button,
  Card,
  Dropdown,
  Empty,
  ErrorNote,
  MultiSelect,
  PageHeader,
  SegmentedControl,
  Skeleton,
  useLoad,
  useToast,
} from "../components/ui"
import { useSession } from "../lib/auth"
import {
  type TimeReportEntry,
  type TimeReportFilters,
  type TimeReportGroupBy,
  type TimeReportOptions,
  api,
} from "../lib/api"
import { DATE_PRESETS, DATE_PRESET_LABELS, type DatePreset, datePreset } from "../lib/dateRange"
import { formatDuration } from "../lib/duration"
import {
  LABEL_NONE,
  NONE,
  filtersFromParams,
  groupByFromParams,
  pageFromParams,
  paramsFromFilters,
  presetOf,
  pruneUnknown,
  queryString,
} from "../lib/timeReport"

// The Time Report. Every filter lives in the URL — the page holds no filter
// state of its own, so a link *is* a saved report, and the back button
// behaves like a filter change (it is one).
//
// The work-items list loads on first visit with the default period (this
// month); nothing here is saved to the database and the only export is the
// generated CSV.

const GROUP_BY_LABELS: Record<TimeReportGroupBy, string> = {
  employee: "Person",
  company: "Company",
  project: "Project",
  sprint: "Sprint",
  epic: "Epic",
  task: "Task",
  parent: "Parent",
  label: "Tag",
  day: "Day",
  week: "Week",
  month: "Month",
}

const GROUP_OPTIONS = (Object.keys(GROUP_BY_LABELS) as TimeReportGroupBy[]).map((value) => ({
  value,
  label: GROUP_BY_LABELS[value],
}))

export function TimeReportPage() {
  const { employee } = useSession()
  const isAdmin = employee.role === "admin" || employee.role === "platform_admin"

  const [searchParams, setSearchParams] = useSearchParams()
  const filters = useMemo(
    () => filtersFromParams(searchParams),
    [searchParams.toString()],
  )
  const groupBy = groupByFromParams(searchParams)
  const page = pageFromParams(searchParams)

  // Every control writes the whole param set — filters, groupBy and page — so
  // a filter change keeps the grouping, a page turn keeps the filters, and the
  // URL is always the report the user is looking at. A filter change is a new
  // query, so it resets the page; a page turn is not.
  const writeParams = (change: Partial<TimeReportFilters> & { groupBy?: TimeReportGroupBy | null; page?: number }, resetPage: boolean) => {
    const params = paramsFromFilters({ ...filters, ...change })
    const nextGroupBy = "groupBy" in change ? change.groupBy : groupBy
    if (nextGroupBy) params.set("groupBy", nextGroupBy)
    const nextPage = resetPage ? 1 : ("page" in change ? change.page ?? 1 : page)
    if (nextPage > 1) params.set("page", String(nextPage))
    setSearchParams(params)
  }
  const patch = (change: Partial<TimeReportFilters>) => writeParams(change, true)

  const options = useLoad(() => api.get<TimeReportOptions>("/time-report/options"), [])

  // Ids the company does not have go as soon as the menus can say so — see
  // pruneUnknown. A replace, not a push: the URL was never a report the user
  // chose, so Back should not return to it.
  const toast = useToast()
  useEffect(() => {
    if (!options.data) return
    const { filters: pruned, removed } = pruneUnknown(filters, options.data)
    if (!removed) return
    const params = paramsFromFilters(pruned)
    if (groupBy) params.set("groupBy", groupBy)
    setSearchParams(params, { replace: true })
    toast(
      removed === 1
        ? "1 filter removed — it isn't in any company you can report on"
        : `${removed} filters removed — they aren't in any company you can report on`,
    )
  }, [options.data, searchParams.toString()])
  const entries = useLoad(
    () =>
      api.get<import("../lib/api").TimeReportEntriesPage>(
        `/time-report/entries${queryString(filters, null, page)}`,
      ),
    [queryString(filters, null, page)],
  )
  // Loaded only when the Grouped view is open — the work-item list is the
  // default and the group sums reconcile to its total, so one fetch per view.
  // The URL's page is the grouped view's page when it is open — switching
  // views resets it, so the two never share a position.
  const groups = useLoad(
    () =>
      groupBy
        ? api.get<import("../lib/api").TimeReportGroups>(
            `/time-report/groups${queryString(filters, groupBy, page)}`,
          )
        : Promise.resolve(null),
    [queryString(filters, groupBy ?? undefined, page)],
  )

  async function exportCsv() {
    // Through the API client so the request carries the auth header — a bare
    // <a href> would be an unauthenticated navigation and 401.
    const csv = await api.raw(`/time-report/export.csv${queryString(filters, groupBy)}`)
    // The server sends a BOM so Excel reads the file as UTF-8, but res.text()
    // strips a leading BOM while decoding — put it back, or the saved file
    // never has one.
    const bom = csv.startsWith("\uFEFF") ? "" : "\uFEFF"
    const url = URL.createObjectURL(new Blob([bom, csv], { type: "text/csv" }))
    const anchor = document.createElement("a")
    anchor.href = url
    anchor.download = `time-report-${filters.from ?? "all"}-${filters.to ?? "time"}.csv`
    anchor.click()
    URL.revokeObjectURL(url)
  }

  const summary = entries.data?.summary
  const preset = presetOf(filters.from, filters.to)

  // Sprint, epic and task options narrow to the selected projects — or, with
  // none picked, to the selected companies' projects; an empty filter leaves
  // them all. The "No …" entries make the grouped view's sentinel drill-downs
  // visible here, so they can be unticked.
  const optionList = options.data
  const companySet = new Set(filters.companyIds)
  const inCompanies = (companyIds: string[]) => (p: { companyId: string }) =>
    !companyIds.length || companyIds.includes(p.companyId)
  const projectScope = (projectIds: string[], companyIds: string[]) =>
    projectIds.length || !companyIds.length
      ? projectIds
      : (optionList?.projects ?? []).filter(inCompanies(companyIds)).map((p) => p.id)
  const projectSet = new Set(projectScope(filters.projectIds, filters.companyIds))
  const inProjects = (projectIds: string[]) => (item: { projectId: string }) =>
    !projectIds.length || projectIds.includes(item.projectId)
  const sprintOptions = [
    { value: NONE, label: "No sprint" },
    ...(optionList?.sprints ?? [])
      .filter((s) => !projectSet.size || projectSet.has(s.projectId))
      .map((s) => ({ value: s.id, label: s.name })),
  ]
  const epicOptions = [
    { value: NONE, label: "No epic" },
    ...(optionList?.epics ?? [])
      .filter((e) => !projectSet.size || projectSet.has(e.projectId))
      .map((e) => ({ value: e.id, label: `${e.key} ${e.title}` })),
  ]
  const taskOptions = (optionList?.tasks ?? [])
    .filter((t) => !projectSet.size || projectSet.has(t.projectId))
    .map((t) => ({ value: t.id, label: `${t.key} ${t.title}` }))
  const parentOptions = [{ value: NONE, label: "No parent" }, ...taskOptions]

  // A project change drops the sprint/epic/task picks that belonged to the
  // projects no longer selected: they would vanish from the menus above while
  // still filtering, and a report filtered by something you cannot see or
  // untick reads as "no time logged". NONE belongs to no project and stays.
  // A company change does the same one level up — its projects go first,
  // then whatever hung off them.
  function changeCompanies(companyIds: string[]) {
    if (!optionList) return patch({ companyIds })
    const allowed = new Set(optionList.projects.filter(inCompanies(companyIds)).map((p) => p.id))
    changeProjects(filters.projectIds.filter((id) => allowed.has(id)), companyIds)
  }
  function changeProjects(projectIds: string[], companyIds = filters.companyIds) {
    if (!optionList) return patch({ projectIds, companyIds })
    const scoped = projectScope(projectIds, companyIds)
    const keep = (ids: string[], items: { id: string; projectId: string }[]) => {
      const allowed = new Set(items.filter(inProjects(scoped)).map((i) => i.id))
      return ids.filter((id) => id === NONE || allowed.has(id))
    }
    patch({
      companyIds,
      projectIds,
      sprintIds: keep(filters.sprintIds, optionList.sprints),
      epicIds: keep(filters.epicIds, optionList.epics),
      taskIds: keep(filters.taskIds, optionList.tasks),
      parentIds: keep(filters.parentIds, optionList.tasks),
    })
  }
  // An admin's report spans every company they are admin of; once it does,
  // the company leads each project's name, since two clients can both have a
  // "Website".
  const multiCompany = new Set((optionList?.projects ?? []).map((p) => p.companyId)).size > 1
  const companyOptions = (optionList?.companies ?? []).map((c) => ({ value: c.id, label: c.name }))
  const projectOptions = (optionList?.projects ?? [])
    .filter((p) => !companySet.size || companySet.has(p.companyId))
    .map((p) => {
      const name = p.keyPrefix ? `${p.keyPrefix} ${p.name}` : p.name
      return { value: p.id, label: multiCompany ? `${p.companyName} · ${name}` : name }
    })
  const labelOptions = [
    { value: LABEL_NONE, label: "No tag" },
    ...(optionList?.labels ?? []).map((l) => ({ value: l, label: l })),
  ]
  const employeeOptions = (optionList?.employees ?? []).map((e) => ({
    value: e.id,
    label: e.name,
  }))

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Time Report"
        subtitle="Time logged across every project — filter it, group it, export it."
        action={
          <div className="flex items-end gap-2">
            <label className="flex flex-col gap-1 text-xs text-muted">
              <span>Period</span>
              <select
                name="period"
                // "" is the placeholder the select shows when the current
                // from/to pair matches no preset — the custom range below.
                value={preset ?? ""}
                onChange={(event) => {
                  const value = event.target.value
                  if (!value) return
                  const range = datePreset(value as DatePreset)
                  patch({ from: range.from, to: range.to })
                }}
                className="w-40 rounded-md border border-border bg-surface px-2 py-1.5 text-sm outline-none focus:border-interactive"
              >
                <option value="">Custom</option>
                {DATE_PRESETS.map((p) => (
                  <option key={p} value={p}>
                    {DATE_PRESET_LABELS[p]}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-xs text-muted">
              From
              <input
                type="date"
                value={filters.from ?? ""}
                onChange={(e) => patch({ from: e.target.value || null })}
                className="rounded-md border border-border bg-surface px-2 py-1.5 text-sm outline-none focus:border-interactive"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs text-muted">
              To
              <input
                type="date"
                value={filters.to ?? ""}
                onChange={(e) => patch({ to: e.target.value || null })}
                className="rounded-md border border-border bg-surface px-2 py-1.5 text-sm outline-none focus:border-interactive"
              />
            </label>
            <Button variant="ghost" onClick={exportCsv} disabled={entries.loading}>
              Export CSV
            </Button>
          </div>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        {/* Only once the report spans several companies — with one, the
            filter could only ever select everything. */}
        {companyOptions.length > 1 && (
          <MultiSelect
            label="Company"
            options={companyOptions}
            selected={filters.companyIds}
            onChange={changeCompanies}
            search
          />
        )}
        <MultiSelect
          label="Project"
          options={projectOptions}
          selected={filters.projectIds}
          onChange={changeProjects}
          search
        />
        <MultiSelect
          label="Sprint"
          options={sprintOptions}
          selected={filters.sprintIds}
          onChange={(sprintIds) => patch({ sprintIds })}
          search
        />
        <MultiSelect
          label="Epic"
          options={epicOptions}
          selected={filters.epicIds}
          onChange={(epicIds) => patch({ epicIds })}
          search
        />
        <MultiSelect
          label="Task"
          options={taskOptions}
          selected={filters.taskIds}
          onChange={(taskIds) => patch({ taskIds })}
          search
        />
        {/* Parent has no menu of its own until a Parent group is drilled
            into — it is that drill-down's filter, shown so it can be undone. */}
        {filters.parentIds.length > 0 && (
          <MultiSelect
            label="Parent"
            options={parentOptions}
            selected={filters.parentIds}
            onChange={(parentIds) => patch({ parentIds })}
            search
          />
        )}
        <MultiSelect
          label="Tag"
          options={labelOptions}
          selected={filters.labels}
          onChange={(labels) => patch({ labels })}
        />
        {/* A developer's report can only ever show their own entries, so the
            user filter would offer options that select nothing. */}
        {isAdmin && (
          <MultiSelect
            label="User"
            options={employeeOptions}
            selected={filters.employeeIds}
            onChange={(employeeIds) => patch({ employeeIds })}
            search
          />
        )}
        <div className="ml-auto flex items-center gap-2">
          <Dropdown
            align="right"
            trigger={({ open, toggle }) => (
              <Button variant="ghost" size="sm" aria-label="Group by" aria-haspopup="menu" aria-expanded={open} onClick={toggle}>
                {groupBy ? GROUP_BY_LABELS[groupBy] : "No grouping"}
              </Button>
            )}
            items={[
              { label: "No grouping", selected: !groupBy, onSelect: () => writeParams({ groupBy: null }, true) },
              ...GROUP_OPTIONS.map((g) => ({
                label: g.label,
                selected: groupBy === g.value,
                onSelect: () => writeParams({ groupBy: g.value }, true),
              })),
            ]}
          />
          <SegmentedControl
            ariaLabel="View"
            value={groupBy ? "grouped" : "items"}
            options={[
              { value: "items", label: "Work items" },
              { value: "grouped", label: "Grouped" },
            ]}
            onChange={(view) =>
              writeParams({ groupBy: view === "grouped" ? (groupBy ?? "employee") : null }, true)
            }
          />
        </div>
      </div>

      <ErrorNote error={entries.error ?? options.error ?? groups.error} />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Total hours" value={formatDuration(entries.data?.totalMinutes ?? 0) || "0m"} />
        <Stat label="Entries" value={String(summary?.entries ?? 0)} />
        <Stat label="People" value={String(summary?.people ?? 0)} />
        <Stat label="Tasks" value={String(summary?.tasks ?? 0)} />
      </div>

      {groupBy ? (
        <GroupedView
          groupBy={groupBy}
          loading={groups.loading}
          data={groups.data ?? null}
          onOpenGroup={patch}
          onPage={(next) => writeParams({ page: next }, false)}
        />
      ) : entries.loading ? (
        <Skeleton className="h-64" />
      ) : (entries.data?.entries.length ?? 0) === 0 ? (
        <Empty
          title="No time logged"
          hint="Nothing matches these filters in this period. Widen the period or clear a filter."
        />
      ) : (
        <EntriesTable
          data={entries.data!}
          multiCompany={multiCompany}
          onPage={(next) => writeParams({ page: next }, false)}
        />
      )}
    </div>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Card className="flex flex-col gap-1 p-4">
      <span className="text-xs text-muted">{label}</span>
      <span className="text-xl font-semibold tabular-nums text-foreground">{value}</span>
    </Card>
  )
}

// No table primitive exists in the house, and one table does not earn one —
// plain markup with the Tailwind tokens the rest of the app reads.

function EntriesTable({
  data,
  multiCompany,
  onPage,
}: {
  data: import("../lib/api").TimeReportEntriesPage
  multiCompany: boolean
  onPage: (page: number) => void
}) {
  const pagination = data.pagination
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-surface-muted text-left text-xs text-muted">
            <th className="px-3 py-2 font-medium">Date</th>
            <th className="px-3 py-2 font-medium">Task</th>
            <th className="px-3 py-2 font-medium">Project</th>
            <th className="px-3 py-2 font-medium">Epic</th>
            <th className="px-3 py-2 font-medium">Sprint</th>
            <th className="px-3 py-2 font-medium">Tags</th>
            <th className="px-3 py-2 font-medium">Author</th>
            <th className="px-3 py-2 text-right font-medium">Hours</th>
            <th className="px-3 py-2 font-medium">Note</th>
          </tr>
        </thead>
        <tbody>
          {data.entries.map((entry: TimeReportEntry) => (
            <tr key={entry.id} className="border-b border-border last:border-0">
              <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-muted tabular-nums">
                {entry.spentOn.slice(0, 10)}
              </td>
              <td className="max-w-72 px-3 py-2">
                <Link to={`/tasks/${entry.task.id}`} className="group flex min-w-0 items-baseline gap-2">
                  <span className="shrink-0 font-mono text-xs text-faint group-hover:text-interactive">
                    {entry.task.key}
                  </span>
                  <span className="truncate text-foreground group-hover:text-interactive">
                    {entry.task.title}
                  </span>
                </Link>
              </td>
              <td className="px-3 py-2">
                <span className="block text-muted">{entry.project.name}</span>
                {multiCompany && <span className="block text-xs text-faint">{entry.project.companyName}</span>}
              </td>
              <td className="max-w-48 truncate px-3 py-2 text-muted">
                {entry.epic ? `${entry.epic.key} ${entry.epic.title}` : "—"}
              </td>
              <td className="px-3 py-2 text-muted">{entry.sprint ?? "—"}</td>
              <td className="px-3 py-2 text-muted">{entry.labels.join(", ") || "—"}</td>
              <td className="px-3 py-2">
                <span className="block text-foreground">{entry.author.name}</span>
                <span className="block text-xs text-faint">{entry.author.email}</span>
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-xs tabular-nums text-foreground">
                {(entry.minutes / 60).toFixed(2)}
              </td>
              <td className="max-w-60 truncate px-3 py-2 text-muted">{entry.note ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between gap-3 border-t border-border px-3 py-2">
          <span className="text-xs text-faint">
            Page {pagination.page} of {pagination.totalPages} · {pagination.totalRows} entries
          </span>
          <div className="flex gap-1">
            <Button variant="subtle" size="sm" disabled={pagination.page <= 1}
              onClick={() => onPage(pagination.page - 1)}>Previous</Button>
            <Button variant="subtle" size="sm" disabled={pagination.page >= pagination.totalPages}
              onClick={() => onPage(pagination.page + 1)}>Next</Button>
          </div>
        </div>
      )}
    </div>
  )
}

function GroupedView({
  groupBy,
  loading,
  data,
  onOpenGroup,
  onPage,
}: {
  groupBy: TimeReportGroupBy
  loading: boolean
  data: import("../lib/api").TimeReportGroups | null
  onOpenGroup: (add: Partial<TimeReportFilters>) => void
  onPage: (page: number) => void
}) {
  const [open, setOpen] = useState<string | null>(null)
  if (loading || !data) return <Skeleton className="h-64" />

  const { rows, totalMinutes, groupedMinutes, pagination } = data
  // Tag rows repeat an entry once per tag, so their share is of the tag total,
  // not the report total. Both come from the server across every page — a sum
  // of the rows on screen would shift as you paged.
  const rowSum = groupBy === "label" ? groupedMinutes : totalMinutes

  return (
    <div className="flex flex-col gap-2">
      {rows.length === 0 ? (
        <Empty title="No time logged" hint="Nothing matches these filters in this period." />
      ) : (
        <div className="divide-y divide-border rounded-lg border border-border">
          {rows.map((row) => {
            const share = rowSum ? Math.round((row.minutes / rowSum) * 100) : 0
            const isOpen = open === row.key
            // The key doubles as the filter value — the sentinels included:
            // the server reads "none" as no sprint/epic/parent and LABEL_NONE
            // as untagged, so every drill-down narrows to exactly its row.
            // Parent goes through parentIds, the direct-parent match, not
            // taskIds, which would add the parent's own time and its
            // grandchildren's. Date buckets filter nothing.
            const filterKey =
              groupBy === "employee" ? { employeeIds: [row.key] }
              : groupBy === "company" ? { companyIds: [row.key] }
              : groupBy === "project" ? { projectIds: [row.key] }
              : groupBy === "sprint" ? { sprintIds: [row.key] }
              : groupBy === "epic" ? { epicIds: [row.key] }
              : groupBy === "task" ? { taskIds: [row.key] }
              : groupBy === "parent" ? { parentIds: [row.key] }
              : groupBy === "label" ? { labels: [row.key] }
              : null
            return (
              <div key={row.key}>
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? null : row.key)}
                  className="flex w-full items-center gap-3 px-3 py-2 text-sm hover:bg-surface-muted"
                >
                  <span className={`shrink-0 text-faint transition-transform ${isOpen ? "rotate-90" : ""}`}>›</span>
                  <span className="min-w-0 flex-1 truncate text-left text-foreground">{row.label}</span>
                  <span className="hidden h-1.5 w-40 shrink-0 overflow-hidden rounded-full bg-surface-muted sm:block">
                    <span
                      className="block h-full rounded-full bg-interactive"
                      style={{ width: `${share}%` }}
                    />
                  </span>
                  <span className="shrink-0 font-mono text-xs text-muted tabular-nums">
                    {formatDuration(row.minutes)}
                  </span>
                  <span className="w-14 shrink-0 text-right text-xs text-faint">
                    {row.entryCount} {row.entryCount === 1 ? "entry" : "entries"}
                  </span>
                </button>
                {isOpen && (
                  <div className="flex flex-col gap-1 bg-surface-muted/50 px-3 py-2 pl-8 text-xs text-muted">
                    {filterKey ? (
                      <button
                        type="button"
                        className="w-fit text-interactive hover:underline"
                        onClick={() => onOpenGroup(filterKey)}
                      >
                        Filter to this group
                      </button>
                    ) : (
                      <span>A time bucket — narrow the period to see its entries.</span>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-faint">
          {groupBy === "label"
            ? `Tag rows repeat an entry once per tag — they sum to ${formatDuration(rowSum)} across tags, not the report's ${formatDuration(totalMinutes)}.`
            : `Total ${formatDuration(totalMinutes)}.`}
        </p>
        {pagination.totalPages > 1 && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-faint">
              Page {pagination.page} of {pagination.totalPages} · {pagination.totalRows} groups
            </span>
            <Button variant="subtle" size="sm" disabled={pagination.page <= 1}
              onClick={() => onPage(pagination.page - 1)}>Previous</Button>
            <Button variant="subtle" size="sm" disabled={pagination.page >= pagination.totalPages}
              onClick={() => onPage(pagination.page + 1)}>Next</Button>
          </div>
        )}
      </div>
    </div>
  )
}
